import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import canonicalgroupService from '../services/canonicalgroupService';
import canonicalService from '../services/canonicalService';
import canonicaltemplateService from '../services/canonicaltemplateService';
import countryService from '../services/countryService';
import './CanonicalGroupForm.css';

function flattenObject(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj || {})) {
    const full = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item !== null && typeof item === 'object') {
          Object.assign(result, flattenObject(item, `${full}.${i}`));
        } else {
          result[`${full}.${i}`] = item != null ? String(item) : '';
        }
      });
    } else if (value !== null && typeof value === 'object') {
      Object.assign(result, flattenObject(value, full));
    } else {
      result[full] = value != null ? String(value) : '';
    }
  }
  return result;
}

function getNestedValue(obj, path) {
  return path.trim().split('.').reduce((acc, key) => {
    if (acc === null || acc === undefined) return null;
    const i = parseInt(key);
    return (!isNaN(i) && Array.isArray(acc)) ? acc[i] : acc?.[key];
  }, obj);
}

function decodeHtmlEntities(html) {
  const ta = document.createElement('textarea');
  ta.innerHTML = html;
  return ta.value;
}

function renderTemplateWithPayload(content, payload) {
  if (!content) return '';
  const decoded = decodeHtmlEntities(content);
  const flat = flattenObject(payload || {});

  // Process {{#each path}}...{{/each}}
  let html = decoded.replace(/\{\{#each ([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g, (match, path, itemTpl) => {
    const arr = getNestedValue(payload, path.trim());
    if (!Array.isArray(arr) || arr.length === 0) return '';
    return arr.map(item => {
      const itemFlat = flattenObject(item);
      return itemTpl.replace(/\{\{([^}]+)\}\}/g, (m, key) => {
        const t = key.trim();
        return itemFlat[t] ?? String(getNestedValue(item, t) ?? '');
      });
    }).join('');
  });

  // Process {{#if key}}...{{/if}}
  html = html.replace(/\{\{#if ([^}]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, key, inner) => {
    const val = flat[key.trim()];
    return (val !== undefined && val !== null && val !== '') ? inner : '';
  });

  // Replace {{key}} variables
  return html.replace(/\{\{([^}]+)\}\}/g, (match, key) => flat[key.trim()] ?? '');
}

function CanonicalGroupForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, canonicalid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canonicalName, setCanonicalName] = useState('');
  const [availableParentGroups, setAvailableParentGroups] = useState([]);
  const [childCanonicalGroups, setChildCanonicalGroups] = useState([]);
  const [formData, setFormData] = useState({
    canonicalid: '',
    name: '',
    parent_id: '',
    payload: {},
    schema: {}
  });
  const [activeJsonTab, setActiveJsonTab] = useState('payload');
  const [canonicalTemplates, setCanonicalTemplates] = useState([]);
  const [countryNames, setCountryNames] = useState({}); // { code: description }
  const [previewTemplate, setPreviewTemplate] = useState(null); // { name, html }

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    // Auto-populate canonicalid if creating from canonical page
    if (canonicalid && isCreateMode) {
      console.log('Setting canonicalid from params:', canonicalid);
      setFormData(prev => ({ ...prev, canonicalid: parseInt(canonicalid) }));
    }
  }, [canonicalid, isCreateMode]);

  // Country code → full name, for displaying template country as a name rather than the ISO code.
  useEffect(() => {
    countryService.getAllCountries()
      .then(res => {
        const map = {};
        (res.data?.countries || []).forEach(c => { map[c.code] = c.description; });
        setCountryNames(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchCanonicalGroup();
      fetchChildCanonicalGroups();
      fetchCanonicalTemplates();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    // Fetch canonical when canonicalid is set
    if (formData.canonicalid || canonicalid) {
      fetchCanonical(formData.canonicalid || canonicalid);
      fetchAvailableParentGroups(formData.canonicalid || canonicalid);
    }
  }, [formData.canonicalid, canonicalid]);

  const fetchCanonical = async (canonicalid) => {
    try {
      const response = await canonicalService.getCanonicalById(canonicalid);
      const canonicalData = response.data;
      setCanonicalName(canonicalData.name || '');
    } catch (err) {
      console.error('Failed to fetch canonical:', err);
    }
  };

  const fetchAvailableParentGroups = async (canonicalid) => {
    try {
      const response = await canonicalgroupService.getAllCanonicalGroups({ canonicalid });
      // Filter out the current group if editing (can't be its own parent)
      const groups = (response.data.canonicalgroups || []).filter(g => g.id !== parseInt(id));
      setAvailableParentGroups(groups);
    } catch (err) {
      console.error('Failed to fetch available parent groups:', err);
    }
  };

  const fetchCanonicalGroup = async () => {
    try {
      setLoading(true);
      const response = await canonicalgroupService.getCanonicalGroupById(id);
      const canonicalgroupData = response.data;

      // Parse JSON strings to objects for JSON editors
      if (canonicalgroupData.payload) {
        try {
          canonicalgroupData.payload = typeof canonicalgroupData.payload === 'string'
            ? JSON.parse(canonicalgroupData.payload)
            : canonicalgroupData.payload;
        } catch (e) {
          console.error('Failed to parse payload:', e);
          canonicalgroupData.payload = {};
        }
      } else {
        canonicalgroupData.payload = {};
      }

      if (canonicalgroupData.schema) {
        try {
          canonicalgroupData.schema = typeof canonicalgroupData.schema === 'string'
            ? JSON.parse(canonicalgroupData.schema)
            : canonicalgroupData.schema;
        } catch (e) {
          console.error('Failed to parse schema:', e);
          canonicalgroupData.schema = {};
        }
      } else {
        canonicalgroupData.schema = {};
      }

      setFormData(canonicalgroupData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch canonical group');
    } finally {
      setLoading(false);
    }
  };

  const fetchChildCanonicalGroups = async () => {
    try {
      const response = await canonicalgroupService.getAllCanonicalGroups({ parent_id: id });
      setChildCanonicalGroups(response.data.canonicalgroups || []);
    } catch (err) {
      console.error('Failed to fetch child canonical groups:', err);
    }
  };

  const fetchCanonicalTemplates = async () => {
    try {
      const response = await canonicaltemplateService.getAll({ canonical_group_id: id });
      setCanonicalTemplates(response.data.canonicaltemplates || []);
    } catch (err) {
      console.error('Failed to fetch canonical templates:', err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await canonicaltemplateService.delete(templateId);
      setCanonicalTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const handlePreviewTemplate = async (e, template) => {
    e.stopPropagation();
    try {
      const res = await canonicaltemplateService.getById(template.id);
      const content = res.data?.content || '';
      const html = renderTemplateWithPayload(content, formData.payload || {});
      setPreviewTemplate({ name: template.name, html });
    } catch (err) {
      console.error('Failed to load template for preview:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayloadChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      payload: newData
    }));
  };

  const handleSchemaChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      schema: newData
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.canonicalid) {
      setError('Canonical ID is required');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      // Log the raw formData to see what we're working with
      console.log('Raw formData before submission:', formData);
      console.log('formData.payload:', formData.payload);
      console.log('formData.schema:', formData.schema);

      // Prepare data for submission - stringify JSON objects
      const submitData = {
        canonicalid: formData.canonicalid,
        name: formData.name,
        parent_id: formData.parent_id || null,
        payload: formData.payload && Object.keys(formData.payload).length > 0 ? JSON.stringify(formData.payload) : null,
        schema: formData.schema && Object.keys(formData.schema).length > 0 ? JSON.stringify(formData.schema) : null
      };

      console.log('Submitting canonical group data:', submitData);
      console.log('Stringified payload:', submitData.payload);
      console.log('Stringified schema:', submitData.schema);

      if (isCreateMode) {
        const response = await canonicalgroupService.createCanonicalGroup(submitData);
        console.log('Canonical group created:', response);
      } else if (isEditMode) {
        const response = await canonicalgroupService.updateCanonicalGroup(id, submitData);
        console.log('Canonical group updated:', response);
      }

      // Navigate back to canonical view
      const targetCanonicalId = canonicalid || formData.canonicalid;
      if (targetCanonicalId) {
        navigate(`/admin/canonical/${targetCanonicalId}`);
      } else {
        navigate('/admin/canonical');
      }
    } catch (err) {
      console.error('Error saving canonical group:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save canonical group');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const targetCanonicalId = canonicalid || formData.canonicalid;
    if (targetCanonicalId) {
      navigate(`/admin/canonical/${targetCanonicalId}`);
    } else {
      navigate('/admin/canonical');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this canonical group?')) {
      return;
    }

    try {
      setLoading(true);
      await canonicalgroupService.deleteCanonicalGroup(id);
      const targetCanonicalId = canonicalid || formData.canonicalid;
      if (targetCanonicalId) {
        navigate(`/admin/canonical/${targetCanonicalId}`);
      } else {
        navigate('/admin/canonical');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete canonical group');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="canonicalgroup-form-container">Loading canonical group...</div>;
  }

  return (
    <div className="canonicalgroup-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && (canonicalName || formData.name) && (
        <div className="breadcrumb">
          <Link to="/admin/canonical" className="breadcrumb-link">
            Canonicals
          </Link>
          <span className="breadcrumb-separator">/</span>
          {canonicalName && formData.canonicalid && (
            <>
              <Link to={`/admin/canonical/${formData.canonicalid}`} className="breadcrumb-link">
                {canonicalName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      <h2>
        {isCreateMode && 'Create New Canonical Group'}
        {isEditMode && 'Edit Canonical Group'}
        {isViewMode && 'View Canonical Group'}
      </h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="parent_id">Parent Canonical Group</label>
            <select
              id="parent_id"
              name="parent_id"
              value={formData.parent_id || ''}
              onChange={handleChange}
              disabled={isViewMode}
            >
              <option value="">None (Root Level)</option>
              {availableParentGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Show timestamps in view mode */}
          {(isViewMode || isEditMode) && formData.created_at && (
            <div className="form-group">
              <label>Created At</label>
              <input
                type="text"
                value={new Date(formData.created_at).toLocaleString()}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>
          )}

          {(isViewMode || isEditMode) && formData.updated_at && (
            <div className="form-group">
              <label>Updated At</label>
              <input
                type="text"
                value={new Date(formData.updated_at).toLocaleString()}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>
          )}
        </div>

        {/* Payload / Schema Tabs */}
        <div className="form-section">
          <div className="json-tabs">
            <button
              type="button"
              className={`json-tab-btn${activeJsonTab === 'payload' ? ' active' : ''}`}
              onClick={() => setActiveJsonTab('payload')}
            >
              Payload
            </button>
            <button
              type="button"
              className={`json-tab-btn${activeJsonTab === 'schema' ? ' active' : ''}`}
              onClick={() => setActiveJsonTab('schema')}
            >
              Schema
            </button>
            <button
              type="button"
              className={`json-tab-btn${activeJsonTab === 'templates' ? ' active' : ''}`}
              onClick={() => setActiveJsonTab('templates')}
            >
              Templates
            </button>
          </div>
          {activeJsonTab === 'payload' && (
            <div className="json-editor-container">
              <JsonEditor
                data={formData.payload || {}}
                setData={handlePayloadChange}
                restrictEdit={isViewMode}
                restrictDelete={isViewMode}
                restrictAdd={isViewMode}
                enableClipboard={true}
                theme={githubDarkTheme}
              />
            </div>
          )}
          {activeJsonTab === 'schema' && (
            <div className="json-editor-container">
              <JsonEditor
                data={formData.schema || {}}
                setData={handleSchemaChange}
                restrictEdit={isViewMode}
                restrictDelete={isViewMode}
                restrictAdd={isViewMode}
                theme={githubDarkTheme}
              />
            </div>
          )}
          {activeJsonTab === 'templates' && (
            <div className="templates-tab-content">
              <div className="templates-tab-actions">
                <button
                  type="button"
                  className="btn-add-template"
                  onClick={() => navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${id}/template/new`)}
                  disabled={!id}
                >
                  + Add Template
                </button>
              </div>
              {canonicalTemplates.length === 0 ? (
                <p className="no-templates">No templates yet. Click "Add Template" to create one.</p>
              ) : (
                <table className="templates-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Country</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {canonicalTemplates.map(template => (
                      <tr key={template.id} className="clickable-row" onClick={() => navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${id}/template/${template.id}/edit`)}>
                        <td>{template.name}</td>
                        <td>{template.country ? (countryNames[template.country] || template.country) : '—'}</td>
                        <td>{template.updated_at ? new Date(template.updated_at).toLocaleString() : '—'}</td>
                        <td className="templates-actions-cell">
                          <button
                            type="button"
                            className="btn-template-preview"
                            onClick={(e) => handlePreviewTemplate(e, template)}
                            disabled={!formData.payload || Object.keys(formData.payload).length === 0}
                            title={!formData.payload || Object.keys(formData.payload).length === 0 ? 'No payload defined' : 'Preview with example payload'}
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            className="btn-template-edit"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${id}/template/${template.id}/edit`); }}
                          >
                            Edit
                          </button>
                          {!isViewMode && (
                            <button
                              type="button"
                              className="btn-template-delete"
                              onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${id}/edit`)} title="Edit Canonical Group">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Canonical Group">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to Canonical">
                ← Back to Canonical
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Canonical Group' : 'Update Canonical Group'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="template-preview-overlay" onClick={() => setPreviewTemplate(null)}>
          <div className="template-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="template-preview-header">
              <span className="template-preview-title">Preview — {previewTemplate.name}</span>
              <button type="button" className="template-preview-close" onClick={() => setPreviewTemplate(null)}>✕ Close</button>
            </div>
            <iframe
              className="template-preview-body"
              srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:0;}</style></head><body>${previewTemplate.html}</body></html>`}
              title="Template Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}

      {/* Child Canonical Groups Section - Only show in view/edit mode */}
      {(isViewMode || isEditMode) && id && childCanonicalGroups.length > 0 && (
        <div className="child-canonicalgroups-section">
          <h3>Child Canonical Groups</h3>
          <div className="child-canonicalgroups-list">
            {childCanonicalGroups.map((childGroup) => (
              <div
                key={childGroup.id}
                className="child-canonicalgroup-card clickable-card"
                onClick={() => navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${childGroup.id}`)}
                title="Click to view"
              >
                <div className="child-canonicalgroup-name">{childGroup.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CanonicalGroupForm;
